import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  ShieldOff,
  TrendingUp,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

type Row = {
  org_id: string;
  group_id: string | null;
  bucket: string;
  calls: number;
  injected: number;
  injection_rate_pct: number;
  proceed_count: number;
  escalate_ctx_count: number;
  escalate_other_count: number;
  abstain_count: number;
  avg_top1: number | null;
  p95_top1: number | null;
  avg_latency_ms: number | null;
  p95_latency_ms: number | null;
  avg_fragments: number | null;
  total_fragments: number;
  errors_401: number;
  errors_5xx: number;
  errors_4xx_other: number;
};

type WindowKey = "1h" | "6h" | "24h";

const WINDOW_HOURS: Record<WindowKey, number> = { "1h": 1, "6h": 6, "24h": 24 };

/**
 * MemoryStabilizationDashboard
 *
 * Real-time view of memory module stabilization:
 *  - calls, 401/5xx, traces, fragments returned, prompt-injection rate
 *  - per org and per group, with selectable window (1h / 6h / 24h)
 *  - auto-refresh every 30s
 *
 * Reads from view `memory_stabilization_hourly` (RLS via security_invoker).
 */
export default function MemoryStabilizationDashboard() {
  const [windowKey, setWindowKey] = useState<WindowKey>("1h");
  const [groupFilter, setGroupFilter] = useState<string>("all");

  const { data: rows, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ["memory-stabilization", windowKey],
    queryFn: async () => {
      const sinceIso = new Date(
        Date.now() - WINDOW_HOURS[windowKey] * 3600 * 1000,
      ).toISOString();
      const { data, error } = await supabase
        .from("memory_stabilization_hourly" as any)
        .select("*")
        .gte("bucket", sinceIso)
        .order("bucket", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as Row[];
    },
    refetchInterval: 30_000,
  });

  const { data: groups } = useQuery({
    queryKey: ["memory-stabilization-groups"],
    queryFn: async () => {
      const { data } = await supabase
        .from("syra_groups")
        .select("id, name, org_id")
        .order("name");
      return data ?? [];
    },
  });

  const groupName = useMemo(() => {
    const m = new Map<string, string>();
    (groups ?? []).forEach((g) => m.set(g.id, g.name));
    return m;
  }, [groups]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    if (groupFilter === "all") return rows;
    if (groupFilter === "none") return rows.filter((r) => !r.group_id);
    return rows.filter((r) => r.group_id === groupFilter);
  }, [rows, groupFilter]);

  // Aggregate KPIs across the visible window
  const kpis = useMemo(() => {
    const calls = filtered.reduce((a, r) => a + r.calls, 0);
    const injected = filtered.reduce((a, r) => a + r.injected, 0);
    const fragments = filtered.reduce((a, r) => a + r.total_fragments, 0);
    const e401 = filtered.reduce((a, r) => a + r.errors_401, 0);
    const e5xx = filtered.reduce((a, r) => a + r.errors_5xx, 0);
    const e4xx = filtered.reduce((a, r) => a + r.errors_4xx_other, 0);
    const top1Vals = filtered.map((r) => r.avg_top1).filter((v): v is number => v !== null);
    const avgTop1 = top1Vals.length
      ? top1Vals.reduce((a, b) => a + b, 0) / top1Vals.length
      : null;
    const latVals = filtered.map((r) => r.avg_latency_ms).filter((v): v is number => v !== null);
    const avgLat = latVals.length ? Math.round(latVals.reduce((a, b) => a + b, 0) / latVals.length) : null;
    return {
      calls,
      injected,
      injectionPct: calls > 0 ? (injected / calls) * 100 : 0,
      fragments,
      avgFragments: calls > 0 ? fragments / calls : 0,
      e401,
      e5xx,
      e4xx,
      errorRate: calls > 0 ? ((e401 + e5xx + e4xx) / Math.max(1, calls + e401 + e5xx + e4xx)) * 100 : 0,
      avgTop1,
      avgLat,
    };
  }, [filtered]);

  // Time-series buckets (hour) — sum across orgs/groups
  const series = useMemo(() => {
    const byBucket = new Map<string, { bucket: string; calls: number; injected: number; e401: number; e5xx: number; fragments: number }>();
    for (const r of filtered) {
      const key = r.bucket;
      const cur = byBucket.get(key) ?? { bucket: key, calls: 0, injected: 0, e401: 0, e5xx: 0, fragments: 0 };
      cur.calls += r.calls;
      cur.injected += r.injected;
      cur.e401 += r.errors_401;
      cur.e5xx += r.errors_5xx;
      cur.fragments += r.total_fragments;
      byBucket.set(key, cur);
    }
    return Array.from(byBucket.values())
      .sort((a, b) => a.bucket.localeCompare(b.bucket))
      .map((x) => ({
        ...x,
        time: new Date(x.bucket).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        injection_pct: x.calls > 0 ? Math.round((x.injected / x.calls) * 1000) / 10 : 0,
      }));
  }, [filtered]);

  // Per-group breakdown rolled up across the window
  const perGroup = useMemo(() => {
    const map = new Map<string, Row & { name: string }>();
    for (const r of filtered) {
      const key = r.group_id ?? "__no_group__";
      const cur = map.get(key);
      if (!cur) {
        map.set(key, {
          ...r,
          calls: r.calls,
          injected: r.injected,
          total_fragments: r.total_fragments,
          errors_401: r.errors_401,
          errors_5xx: r.errors_5xx,
          errors_4xx_other: r.errors_4xx_other,
          name: r.group_id ? groupName.get(r.group_id) ?? r.group_id.slice(0, 8) : "(sem grupo)",
        });
      } else {
        cur.calls += r.calls;
        cur.injected += r.injected;
        cur.total_fragments += r.total_fragments;
        cur.errors_401 += r.errors_401;
        cur.errors_5xx += r.errors_5xx;
        cur.errors_4xx_other += r.errors_4xx_other;
      }
    }
    return Array.from(map.values())
      .map((g) => ({
        ...g,
        injection_pct: g.calls > 0 ? Math.round((g.injected / g.calls) * 1000) / 10 : 0,
      }))
      .sort((a, b) => b.calls - a.calls);
  }, [filtered, groupName]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32" />
        <Skeleton className="h-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">Estabilização do módulo de memória</h2>
          <p className="text-sm text-muted-foreground">
            Métricas em tempo real (auto-refresh 30s) • janela {windowKey} • {filtered.length} buckets agregados
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Tabs value={windowKey} onValueChange={(v) => setWindowKey(v as WindowKey)}>
            <TabsList>
              <TabsTrigger value="1h">1h</TabsTrigger>
              <TabsTrigger value="6h">6h</TabsTrigger>
              <TabsTrigger value="24h">24h</TabsTrigger>
            </TabsList>
          </Tabs>
          <Select value={groupFilter} onValueChange={setGroupFilter}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Grupo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os grupos</SelectItem>
              <SelectItem value="none">(sem grupo)</SelectItem>
              {(groups ?? []).map((g) => (
                <SelectItem key={g.id} value={g.id}>
                  {g.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="outline"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <RefreshCw className={`h-4 w-4 ${isRefetching ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
        <Kpi
          icon={<Activity className="h-4 w-4" />}
          label="Calls"
          value={kpis.calls.toLocaleString("pt-BR")}
          tone="default"
        />
        <Kpi
          icon={<TrendingUp className="h-4 w-4" />}
          label="Taxa de injeção"
          value={`${kpis.injectionPct.toFixed(1)}%`}
          tone={kpis.injectionPct >= 50 ? "ok" : kpis.injectionPct >= 20 ? "warn" : "crit"}
          hint={`${kpis.injected} de ${kpis.calls} chamadas alimentaram o prompt`}
        />
        <Kpi
          icon={<CheckCircle2 className="h-4 w-4" />}
          label="Fragments"
          value={kpis.fragments.toLocaleString("pt-BR")}
          hint={`${kpis.avgFragments.toFixed(1)} por call`}
        />
        <Kpi
          icon={<TrendingUp className="h-4 w-4" />}
          label="Top1 médio"
          value={kpis.avgTop1 != null ? kpis.avgTop1.toFixed(3) : "—"}
          tone={
            kpis.avgTop1 == null
              ? "default"
              : kpis.avgTop1 >= 0.5
                ? "ok"
                : kpis.avgTop1 >= 0.2
                  ? "warn"
                  : "crit"
          }
          hint="≥0.5 indica busca vetorial saudável"
        />
        <Kpi
          icon={<ShieldOff className="h-4 w-4" />}
          label="401 / Auth"
          value={kpis.e401.toLocaleString("pt-BR")}
          tone={kpis.e401 === 0 ? "ok" : kpis.e401 < 5 ? "warn" : "crit"}
        />
        <Kpi
          icon={<AlertTriangle className="h-4 w-4" />}
          label="5xx / Errors"
          value={kpis.e5xx.toLocaleString("pt-BR")}
          tone={kpis.e5xx === 0 ? "ok" : kpis.e5xx < 3 ? "warn" : "crit"}
          hint={kpis.e4xx > 0 ? `+${kpis.e4xx} outros 4xx` : undefined}
        />
      </div>

      {/* Time-series chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Calls × injetadas no prompt × erros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gCalls" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gInj" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--chart-2, 142 71% 45%))" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="hsl(var(--chart-2, 142 71% 45%))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="time" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    color: "hsl(var(--popover-foreground))",
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="calls" name="Calls" stroke="hsl(var(--primary))" fill="url(#gCalls)" />
                <Area
                  type="monotone"
                  dataKey="injected"
                  name="Injetadas"
                  stroke="hsl(var(--chart-2, 142 71% 45%))"
                  fill="url(#gInj)"
                />
                <Area type="monotone" dataKey="e401" name="401" stroke="hsl(var(--destructive))" fillOpacity={0} />
                <Area
                  type="monotone"
                  dataKey="e5xx"
                  name="5xx"
                  stroke="hsl(var(--destructive))"
                  strokeDasharray="4 4"
                  fillOpacity={0}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Per-group breakdown */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Por grupo • janela {windowKey}</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {perGroup.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              Nenhuma chamada de memory-search registrada nesta janela.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Grupo</TableHead>
                  <TableHead className="text-right">Calls</TableHead>
                  <TableHead className="text-right">Injetadas</TableHead>
                  <TableHead className="text-right">% inj.</TableHead>
                  <TableHead className="text-right">Fragments</TableHead>
                  <TableHead className="text-right">Avg top1</TableHead>
                  <TableHead className="text-right">P95 lat</TableHead>
                  <TableHead className="text-right">401</TableHead>
                  <TableHead className="text-right">5xx</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {perGroup.map((g) => {
                  const lastBucket = filtered
                    .filter((r) => (r.group_id ?? "__no_group__") === (g.group_id ?? "__no_group__"))
                    .sort((a, b) => b.bucket.localeCompare(a.bucket))[0];
                  return (
                    <TableRow key={g.group_id ?? "no-group"}>
                      <TableCell className="font-medium">{g.name}</TableCell>
                      <TableCell className="text-right tabular-nums">{g.calls}</TableCell>
                      <TableCell className="text-right tabular-nums">{g.injected}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        <Badge
                          variant={
                            g.injection_pct >= 50
                              ? "default"
                              : g.injection_pct >= 20
                                ? "secondary"
                                : "destructive"
                          }
                        >
                          {g.injection_pct.toFixed(1)}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{g.total_fragments}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {lastBucket?.avg_top1 != null ? lastBucket.avg_top1.toFixed(3) : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {lastBucket?.p95_latency_ms ?? "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {g.errors_401 > 0 ? (
                          <Badge variant="destructive">{g.errors_401}</Badge>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {g.errors_5xx > 0 ? (
                          <Badge variant="destructive">{g.errors_5xx}</Badge>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {g.group_id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setGroupFilter(g.group_id!)}
                          >
                            Filtrar
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({
  icon,
  label,
  value,
  tone = "default",
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone?: "default" | "ok" | "warn" | "crit";
  hint?: string;
}) {
  const border =
    tone === "ok"
      ? "border-emerald-500/30"
      : tone === "warn"
        ? "border-amber-500/40"
        : tone === "crit"
          ? "border-destructive/40"
          : "border-border";
  return (
    <Card className={border}>
      <CardContent className="p-3 space-y-1">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
          {icon}
          {label}
        </div>
        <div className="text-2xl font-bold tabular-nums">{value}</div>
        {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );
}
